"use client";

import { rhfResolver } from "@/lib/form";
import { useMutation } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import { reviewSchema, type ProductSummary, type ReviewInput } from "@plexstore/types";

import { Field, FormError } from "@/components/shared/form-field";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, errorMessage } from "@/lib/api/errors";
import { cn } from "@/lib/utils";
import { reviewService } from "@/services/client";

interface ReviewFormProps {
  productId?: string;
  products?: ProductSummary[];
  minimumLength: number;
  onDone: () => void;
}

export function ReviewForm({ productId, products, minimumLength, onDone }: ReviewFormProps) {
  const schema = reviewSchema(minimumLength);
  const form = useForm<ReviewInput>({
    resolver: rhfResolver(schema),
    defaultValues: { productId: productId ?? "", rating: 5, comment: "" },
  });
  const mutation = useMutation({
    mutationFn: (values: ReviewInput) => reviewService.create(values),
    onSuccess: () => {
      toast.success("Thanks for your review!");
      onDone();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.kind === "validation") {
        for (const [field, messages] of Object.entries(e.fieldErrors)) {
          form.setError(field as keyof ReviewInput, { message: messages[0] });
        }
      }
    },
  });
  const commentLength = form.watch("comment")?.length ?? 0;

  return (
    <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className="space-y-5">
      {!productId && (
        <Field label="Product" required error={form.formState.errors.productId?.message}>
          <Controller
            control={form.control}
            name="productId"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full rounded-xl">
                  <SelectValue placeholder="Choose a product you own" />
                </SelectTrigger>
                <SelectContent>
                  {(products ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      )}
      <Field label="Rating" required error={form.formState.errors.rating?.message}>
        <Controller
          control={form.control}
          name="rating"
          render={({ field }) => (
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => field.onChange(n)} aria-label={`${n} stars`} className="rounded-lg p-1 transition hover:scale-110">
                  <Star className={cn("size-7", n <= field.value ? "fill-gold text-gold" : "text-white/20")} />
                </button>
              ))}
            </div>
          )}
        />
      </Field>
      <Field label="Your review" required error={form.formState.errors.comment?.message} hint={`${commentLength}/${minimumLength} characters minimum`}>
        <Textarea {...form.register("comment")} rows={5} placeholder="Tell others what you think…" className="rounded-xl" />
      </Field>
      <FormError message={mutation.error && !(mutation.error instanceof ApiError && mutation.error.kind === "validation") ? errorMessage(mutation.error) : null} />
      <Button type="submit" className="w-full rounded-xl" disabled={mutation.isPending}>
        {mutation.isPending ? "Submitting…" : "Submit review"}
      </Button>
    </form>
  );
}
